import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Loader } from "@/assets/icon/loader";

const buttonVariants = cva(
  "inline-flex items-center px-4 justify-center whitespace-nowrap rounded-[10px] text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none dark:ring-offset-neutral-950 dark:focus-visible:ring-neutral-300",
  {
    variants: {
      variant: {
        system:
          "w-full bg-[#0064FF] border-none hover:bg-blue-medium text-white disabled:bg-black/10 disabled:text-black/20",
        default:
          "bg-neutral-900 text-neutral-50 hover:bg-neutral-900/90 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50/90",
        destructive:
          "bg-red-500 text-neutral-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-neutral-50 dark:hover:bg-red-900/90",
        outline:
          "text-blue-medium border-none bg-white hover:bg-blue-medium/5 disabled:bg-white disabled:text-black/30",
        secondary:
          "bg-neutral-100 text-neutral-900 hover:bg-neutral-100/80 dark:bg-neutral-800 dark:text-neutral-50 dark:hover:bg-neutral-800/80",
        ghost:
          "hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
        link: "text-black/50 bg-transparent outline-none hover:text-blue-medium",
      },
      size: {
        system: "text-2xl py-[30px] h-[60px] rounded-[10px]",
        medium: "py-[10px] h-[40px] rounded-[10px] text-[20px]",
        default: "h-10 px-4 py-2",
        sm: "text-[22px] px-4 h-[46px] rounded-[8px]",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    let defaultClass = buttonVariants({ variant, size, className });

    if (variant === "outline" && size === "sm") {
      defaultClass =
        defaultClass
          .replace(" text-blue-medium", " text-blue-common")
          .replace(" hover:bg-blue-medium/5", "") + " hover:text-blue-medium";
    }

    if (loading) {
      props.disabled = true;
      props.children = <Loader />;

      switch (variant) {
        case "system":
          defaultClass = defaultClass
            .replace(" bg-blue-common", " bg-blue-medium")
            .replace(" disabled:bg-black/10 disabled:text-black/20", "");
          break;
        case "outline":
          defaultClass =
            size !== "sm"
              ? defaultClass
                  .replace(" bg-white", " bg-blue-medium/5")
                  .replace(" disabled:bg-white disabled:text-black/30", "")
              : defaultClass
                  .replace(" hover:text-blue-medium", "")
                  .replace(" disabled:text-black/30", "");
          break;
        case "link":
          defaultClass = defaultClass.replace(
            " hover:text-blue-medium",
            " text-black/50"
          );
          break;
      }
    }

    return <Comp className={cn(defaultClass)} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
